import { useCallback, useMemo, useState } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Group,
  Paper,
  Select,
  TextInput,
  Table,
  Loader,
  Center,
  Button,
  Alert,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch, IconPlus, IconAlertCircle } from "@tabler/icons-react";
import {
  PortalAccessGrant,
  PortalGrantState,
  PortalSubjectType,
  ListGrantsFilters,
} from "../../../services/admin/portalAccess.service";
import { usePortalAccessGrants } from "../../../hooks/admin/usePortalAccess";
import { PortalGrantAdminRow } from "../../../components/admin/portal-access/PortalGrantAdminRow";
import { AddPortalGrantWithSubjectModal } from "../../../components/admin/portal-access/AddPortalGrantWithSubjectModal";
import { EditPortalGrantModal } from "../../../components/admin/portal-access/EditPortalGrantModal";

const SUBJECT_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "All Subjects" },
  { value: "physician", label: "Physician" },
  { value: "practice", label: "Practice" },
  { value: "surgery_center", label: "Surgery Center" },
];

const STATE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "All States" },
  { value: "invited", label: "Invited" },
  { value: "active", label: "Active" },
  { value: "revoked", label: "Revoked" },
];

export default function PortalAccessAdminPage() {
  const [subjectType, setSubjectType] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [addOpened, setAddOpened] = useState(false);
  const [editingGrant, setEditingGrant] = useState<PortalAccessGrant | null>(
    null
  );

  const filters: ListGrantsFilters = useMemo(
    () => ({
      ...(subjectType && {
        subjectType: subjectType as PortalSubjectType,
      }),
      ...(state && { state: state as PortalGrantState }),
      ...(debouncedSearch && { search: debouncedSearch }),
    }),
    [subjectType, state, debouncedSearch]
  );

  const { data: grants, isLoading, isError } = usePortalAccessGrants(filters);

  const handleSubjectTypeChange = useCallback((value: string | null) => {
    setSubjectType(value ?? "");
  }, []);

  const handleStateChange = useCallback((value: string | null) => {
    setState(value ?? "");
  }, []);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.currentTarget.value);
    },
    []
  );

  const handleOpenAdd = useCallback(() => {
    setAddOpened(true);
  }, []);

  const handleCloseAdd = useCallback(() => {
    setAddOpened(false);
  }, []);

  const handleEdit = useCallback((grant: PortalAccessGrant) => {
    setEditingGrant(grant);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditingGrant(null);
  }, []);

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="xl">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Portal Access</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage portal access grants across physicians, practices, and
                surgery centers
              </Text>
            </div>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleOpenAdd}
            >
              Add Grant
            </Button>
          </Group>

          <Paper p="md" withBorder>
            <Group grow align="end">
              <Select
                label="Subject type"
                data={SUBJECT_TYPE_OPTIONS}
                value={subjectType}
                onChange={handleSubjectTypeChange}
                clearable
              />
              <Select
                label="State"
                data={STATE_OPTIONS}
                value={state}
                onChange={handleStateChange}
                clearable
              />
              <TextInput
                label="Search by email"
                placeholder="email substring"
                leftSection={<IconSearch size={14} />}
                value={search}
                onChange={handleSearchChange}
              />
            </Group>
          </Paper>

          <Paper p="md" withBorder>
            {isLoading ? (
              <Center py="xl">
                <Loader size="md" />
              </Center>
            ) : isError ? (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
                title="Couldn't load grants"
                role="alert"
              >
                The portal access list failed to load. Please refresh and try
                again.
              </Alert>
            ) : !grants || grants.length === 0 ? (
              <Text size="sm" c="dimmed" py="md" ta="center">
                No portal access grants match the current filters.
              </Text>
            ) : (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Identity</Table.Th>
                    <Table.Th>Subject</Table.Th>
                    <Table.Th>State</Table.Th>
                    <Table.Th>Notifications</Table.Th>
                    <Table.Th>Last Login</Table.Th>
                    <Table.Th style={{ width: 60 }} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {grants.map((grant) => (
                    <PortalGrantAdminRow
                      key={grant.id}
                      grant={grant}
                      onEdit={handleEdit}
                    />
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Stack>
      </Box>

      <AddPortalGrantWithSubjectModal
        opened={addOpened}
        onClose={handleCloseAdd}
      />
      <EditPortalGrantModal
        opened={editingGrant !== null}
        onClose={handleCloseEdit}
        grant={editingGrant}
      />
    </Container>
  );
}
